import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import copyToClipboard from 'copy-to-clipboard';
import { useState } from 'react';
import { useNotify } from 'react-admin';
import { AppArtFile, artRecipeOf } from '../types';

/**
 * O pedido de ajuste vira ARGUMENTO DE SHELL, e é texto livre de quem atende:
 * aspas, apóstrofo e quebra de linha entram nele com naturalidade. Aspas
 * simples protegem tudo menos o próprio apóstrofo — que fecha, escapa e reabre.
 * Sem isto, um pedido como «tira o d'água» quebra o comando na mão de quem
 * colar, e o erro aparece no terminal, longe de quem escreveu.
 *
 * A quebra de linha vira espaço antes: ela é legal dentro das aspas, mas faz o
 * comando copiado chegar picado no terminal.
 */
const aspas = (texto: string) => `'${texto.replace(/\s+/g, ' ').trim().replace(/'/g, "'\\''")}'`;

/** «Modelo C-G · logo + manchete · degradê — rodada 10 ★» */
const rotuloDoModelo = (file: AppArtFile) => [
  `Modelo ${file.modelKey}`,
  artRecipeOf(file)?.nome ? `· ${artRecipeOf(file)!.nome}` : '',
  file.generationRound != null ? `— rodada ${file.generationRound}` : '',
  file.isPrimary ? '★' : '',
].filter(Boolean).join(' ');

interface Props {
  open: boolean;
  onClose: () => void;
  chainId: string;
  chainName: string;
  /** As folhas da rede, para o seletor. Sem nenhuma, o diálogo não abre. */
  models: AppArtFile[];
  /** O modelo do card que abriu. Ausente, cai na ★ — ou na rodada mais nova. */
  initial?: AppArtFile | null;
}

/**
 * O ajuste é o pedido que aparece depois que o cliente escolhe: não é "faz
 * outra", é "essa, com o logo maior". O gerador tem um modo para isso — pega o
 * HTML que gerou aquele PNG, abre a folha que o cliente viu e devolve o mesmo
 * desenho com a correção.
 *
 * Como o gerador roda na máquina de quem opera (Playwright + a CLI do Claude
 * autenticada), esta tela não executa nada: ela monta o comando. O que ela
 * resolve é o que ninguém decora — a chave do modelo, o número da rodada e o
 * escape do texto do cliente.
 */
const AdjustDialog = ({ open, onClose, chainId, chainName, models, initial }: Props) => {
  const notify = useNotify();
  const [fileId, setFileId] = useState('');
  const [pedido, setPedido] = useState('');
  const [dryRun, setDryRun] = useState(false);
  // Mesmo padrão da geração: medir e repetir custa duas chamadas do agente por
  // tentativa, e no ajuste a régua da manchete é a que mais reprova.
  const [semAvaliacao, setSemAvaliacao] = useState(true);

  if (!models.length) { return null; }

  const escolhido = models.find(file => file.id === fileId)
    || (initial && models.find(file => file.id === initial.id))
    || models.find(file => file.isPrimary)
    || models[0];

  /* O gerador só ajusta o que tem HTML de autor, e ele falta por dois motivos
     diferentes — com saídas diferentes. Dizer aqui poupa a viagem ao terminal
     para descobrir. Rodada sem `recipe` nenhum é anterior à coluna: aí não dá
     para saber, e o silêncio é honesto. */
  const recipe = artRecipeOf(escolhido);
  const doSorteio = recipe?.receita?.origem === 'sorteio';
  const semAutor = !!recipe && !doSorteio && !recipe.interno?.welcome;

  const flags = [
    semAvaliacao ? '--sem-avaliacao' : '',
    `--chain ${chainId}`,
    `--ajustar ${escolhido.modelKey}`,
    `--pedido ${aspas(pedido || 'o que mudar')}`,
    /* A rodada vai SEMPRE que o servidor souber dizer qual é. O padrão do
       gerador é "a última em out/", que é a última daquela MÁQUINA — e quem
       ajusta um modelo de duas rodadas atrás está olhando esta tela, não a
       pasta. Fixar aqui tira a diferença entre as duas contagens. */
    escolhido.generationRound != null ? `--rodada ${escolhido.generationRound}` : '',
    dryRun ? '--dry-run' : '',
  ].filter(Boolean).join(' ');

  // O `--` é do npm: sem ele as flags são comidas pelo próprio npm.
  const command = `npm run ajuste -- ${flags}`;

  const copy = () => {
    copyToClipboard(command);
    notify('Comando copiado — cole no terminal.', { type: 'info' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Pedir ajuste — {chainName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Isto <strong>não</strong> gera arte nova: o gerador pega o desenho deste modelo,
          abre a folha que o cliente viu e aplica só a mudança pedida — o resto
          volta igual. Sai uma rodada nova com a mesma chave de modelo, então o
          antes e o depois ficam lado a lado aqui.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            select
            size="small"
            label="Qual modelo"
            value={escolhido.id}
            onChange={event => setFileId(event.target.value)}
            sx={{ minWidth: 380 }}
            helperText="★ é o modelo que o cliente escolheu"
          >
            {models.map(file => (
              <MenuItem key={file.id} value={file.id}>{rotuloDoModelo(file)}</MenuItem>
            ))}
          </TextField>

          <Box>
            <Tooltip title="Marcado (padrão): o ajuste sai numa tentativa, sem medir contraste da manchete, calma no topo e na base e motivo. Desmarque para o gerador medir e repetir até passar — é mais lento.">
              <FormControlLabel
                control={<Checkbox checked={semAvaliacao} onChange={(_, v) => setSemAvaliacao(v)} />}
                label="Pular a avaliação final (--sem-avaliacao)"
              />
            </Tooltip>
            <FormControlLabel
              control={<Checkbox checked={dryRun} onChange={(_, v) => setDryRun(v)} />}
              label="Só gerar local, sem subir (--dry-run)"
            />
          </Box>
        </Box>

        {doSorteio && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este modelo saiu do motor determinístico, que desenha por template e não
            deixa HTML para corrigir — o gerador vai recusar o ajuste. Gere uma rodada
            nova em “Gerar modelos” (o motor de lá é o agente) e ajuste em cima dela.
          </Alert>
        )}

        {semAutor && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta rodada é anterior ao gerador passar a guardar o desenho do modelo, então
            não há o que corrigir — o ajuste vale das rodadas novas em diante. Gere uma
            rodada em “Gerar modelos” e peça o ajuste em cima dela.
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="O que o cliente quer diferente"
          placeholder="o logo ficou pequeno e a manchete está encostando nos botões"
          value={pedido}
          onChange={event => setPedido(event.target.value)}
          helperText="Nas palavras dele. O agente olha a arte enquanto corrige, então vale falar do que se vê — tamanho, posição, cor, o que sobra e o que falta."
        />

        <Box
          sx={{
            mt: 2,
            p: 2, borderRadius: 1, backgroundColor: 'grey.900', color: 'grey.100',
            fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {command}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Rode dentro do <code>pontuax-art-generator</code>. Leva alguns minutos: são duas
          peças (welcome e header), e em cada uma o agente abre a imagem antes de mexer.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Tooltip title={pedido.trim() ? '' : 'Escreva o que mudar primeiro'}>
          <span>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              disabled={!pedido.trim()}
              onClick={copy}
            >
              Copiar comando
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustDialog;
